import { XMLParser } from "fast-xml-parser";

export interface CustomerSurveyReview {
  dEntered: string;
  tComments: string;
  nAppId: number;
  tApplicantFirstName: string;
  tApplicantLastName: string;
  nServiceAnswer: number;
  tCoApplicantFirstName: string;
  tCoApplicantLastName: string;
  tUsername: string;
}

export interface TrustPilotRating {
  rating: number;
  reviews: number;
}

export const getReviews = async (
  page: number = 1,
  pageSize: number = 10,
  sortBy: string = "dEntered",
  sortDir: string = "DESC",
): Promise<{
  data: CustomerSurveyReview[];
  totalReviews: number;
  averageCustomerRating: number;
}> => {
  const baseUrl = process.env.NEXT_PUBLIC_WIZARD_API_BASE_URL;

  // build URLs
  const reviewsUrl = `${baseUrl}/customer-survey/reviews/all?sortBy=${sortBy}&sortDir=${sortDir}&offset=${
    (page - 1) * pageSize
  }&pageSize=${pageSize}`;

  const summaryUrl = `${baseUrl}/customer-survey/ratings/summary`;

  // fetch in parallel
  const [reviewsRes, summaryRes] = await Promise.all([
    fetch(reviewsUrl),
    fetch(summaryUrl),
  ]);

  if (!reviewsRes.ok) {
    throw new Error("Failed to fetch reviews");
  }
  if (!summaryRes.ok) {
    throw new Error("Failed to fetch summary");
  }

  const reviewsData = await reviewsRes.json();
  const summaryData = await summaryRes.json();

  return {
    data: reviewsData.data,
    totalReviews: summaryData.data.totalReviews,
    averageCustomerRating: summaryData.data.averageCustomerRating,
  };
};

// Changes for rates API
export interface RateData {
  updatedDate: string;
  rates: {
    loanType: string;
    rate: string;
    apr: string;
    ctaUrl: string;
    ctaLabel: string;
  }[];
  disclosureData?: {
    loanAmount?: string | null;
    creditScore?: string | null;
    ltv?: string | null;
    county?: string | null;
    state?: string | null;
    updatedDate?: string | null;
  };
}

export const getRates = async (): Promise<RateData | null> => {
  try {
    const apiUrl = "https://amerisavecurrentrates.azureedge.net/xml/v3/";
    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error("Failed to fetch rates");
    }

    const xmlText = await response.text();
    const parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: "",
      // Single-entry feeds would otherwise parse as objects instead of arrays
      isArray: (name) => name === "scenario" || name === "item",
    });
    const xml = parser.parse(xmlText);

    const updatedDate = xml?.rates?.updated || "";
    const scenarios = xml?.rates?.scenarios?.scenario ?? [];
    const cashoutScenario = scenarios.find(
      (scenario: any) => scenario.tTransactionType === "Cashout",
    );

    if (!cashoutScenario) return null;

    const items = cashoutScenario.item ?? [];

    let rate30 = "",
      apr30 = "",
      rate15 = "",
      apr15 = "";

    for (const item of items) {
      const desc = item.tProductDesc || "";

      if (desc.includes("30 Year Fixed")) {
        rate30 = item.tRatePct || "";
        apr30 = item.tAprPct || "";
      }

      if (desc.includes("15 Year Fixed")) {
        rate15 = item.tRatePct || "";
        apr15 = item.tAprPct || "";
      }
    }

    const disclosureData = {
      loanAmount: "$" + Number(cashoutScenario.nLoanAmount).toLocaleString(),
      creditScore: cashoutScenario.nCreditScore + " FICO",
      ltv: cashoutScenario.nLtv + "% LTV",
      county: cashoutScenario.tPropertyCounty + " County",
      state: cashoutScenario.tPropertyStateCode,
      updatedDate,
    };

    return {
      rates: [
        {
          loanType: "30-year fixed",
          rate: rate30 || "-",
          apr: apr30 || "-",
          ctaUrl:
            "https://apply.amerisave.com/quote/v1/home?_gl=1*4dc40k*_gcl_au*MTEwNjY0Mzk3My4xNzU5ODMzMzc2*_ga*MTM0NjIyMDM2NC4xNzU5ODMzMzc2*_ga_WRQCR2TLWN*czE3NTk4MzMzNzYkbzEkZzEkdDE3NTk4MzM0NjckajMzJGwwJGgw&sessionId=5JK007H3&flowId=homeFlow&stepId=HomeStep",
          ctaLabel: "Get Personalized Quote",
        },
        {
          loanType: "15-year fixed",
          rate: rate15 || "-",
          apr: apr15 || "-",
          ctaUrl:
            "https://apply.amerisave.com/quote/v1/home?_gl=1*4dc40k*_gcl_au*MTEwNjY0Mzk3My4xNzU5ODMzMzc2*_ga*MTM0NjIyMDM2NC4xNzU5ODMzMzc2*_ga_WRQCR2TLWN*czE3NTk4MzMzNzYkbzEkZzEkdDE3NTk4MzM0NjckajMzJGwwJGgw&sessionId=5JK007H3&flowId=homeFlow&stepId=HomeStep",
          ctaLabel: "Get Personalized Quote",
        },
      ],
      updatedDate,
      disclosureData,
    };
  } catch (err) {
    console.error("Error fetching rates:", err);
    return null;
  }
};

export const getTrustPilotRating = async (): Promise<TrustPilotRating> => {
  const defaultRating: TrustPilotRating = {
    rating: 4.5, // default rating
    reviews: 15245, // default review count
  };

  try {
    const baseUrl = process.env.NEXT_PUBLIC_WIZARD_API_BASE_URL;
    const apiUrl = `${baseUrl}/trustpilot/ratings`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn("Failed to fetch TrustPilot rating, using default values");
      return defaultRating;
    }

    const json = await response.json();

    // If API returns invalid data, fallback to defaults
    if (!json?.data?.rating || !json?.data?.reviews) {
      return defaultRating;
    }

    return {
      rating: json.data.rating,
      reviews: json.data.reviews,
    };
  } catch (err) {
    console.error(
      "Error fetching TrustPilot rating, using default values:",
      err,
    );
    return defaultRating;
  }
};
